import React from 'react';
import { useNavigate } from 'react-router-dom';
import { theme, alpha } from '../../theme';
import type { PendingActionMatch } from '../../hooks/useHomeFeed';

function getUrgencyState(expiresAt: string) {
  const now = new Date();
  const expires = new Date(expiresAt);
  const hoursRemaining = (expires.getTime() - now.getTime()) / (1000 * 60 * 60);
  const totalHours = 72;
  const progressPercent = Math.max(0, Math.min(100, (hoursRemaining / totalHours) * 100));

  if (hoursRemaining <= 0) {
    return { level: 'expired' as const, color: '#9CA3AF', label: 'Expirado', progressPercent: 0 };
  }
  if (hoursRemaining <= 24) {
    return { level: 'critical' as const, color: '#EF4444', label: `${Math.floor(hoursRemaining)}h restantes`, progressPercent };
  }
  if (hoursRemaining <= 48) {
    return { level: 'warning' as const, color: '#F59E0B', label: `${Math.floor(hoursRemaining / 24)}d restantes`, progressPercent };
  }
  return { level: 'safe' as const, color: '#10B981', label: `${Math.floor(hoursRemaining / 24)}d restantes`, progressPercent };
}

function formatFinishedDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

interface PendingActionsCardProps {
  pendingActions?: PendingActionMatch[];
}

export default function PendingActionsCard({ pendingActions = [] }: PendingActionsCardProps) {
  const navigate = useNavigate();

  const active = pendingActions.filter((m) => getUrgencyState(m.expiresAt).level !== 'expired');
  if (!active.length) return null;

  const totalActions = active.reduce((acc, m) => acc + m.actions.length, 0);

  return (
    <div
      style={{
        marginBottom: theme.spacing.md,
        padding: theme.spacing.md,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        border: `1px solid ${theme.colors.border}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: 4 }}>
        <span style={{ fontSize: 22 }}>📋</span>
        <span style={{ color: theme.colors.text, fontWeight: 700, flex: 1 }}>Acciones Pendientes</span>
        <span
          style={{
            backgroundColor: `${alpha('warning', '20')}`,
            color: theme.colors.warning,
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {totalActions}
        </span>
      </div>
      <p style={{ margin: '0 0 12px 0', color: theme.colors.textSecondary, fontSize: 13 }}>
        Completá estas acciones de tus partidos finalizados
      </p>
      <div style={{ display: 'flex', gap: theme.spacing.sm, overflowX: 'auto', paddingBottom: 8 }}>
        {active.map((match, index) => {
          const urgency = getUrgencyState(match.expiresAt);
          return (
            <button
              type="button"
              key={`${match.matchId}-${index}`}
              tabIndex={0}
              onClick={() => navigate(`/match/${match.matchId}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/match/${match.matchId}`)}
              style={{
                minWidth: 240,
                padding: theme.spacing.md,
                backgroundColor: theme.colors.card,
                borderRadius: 12,
                border: `1px solid ${theme.colors.border}`,
                borderLeftWidth: 4,
                borderLeftColor: urgency.color,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: theme.colors.card,
                textAlign: 'left',
                width: '100%',
              }}
            >
              <div style={{ marginBottom: theme.spacing.sm }}>
                <div
                  style={{
                    height: 6,
                    borderRadius: 999,
                    backgroundColor: `${urgency.color}20`,
                    overflow: 'hidden',
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${urgency.progressPercent}%`,
                      backgroundColor: urgency.color,
                      borderRadius: 999,
                    }}
                  />
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: urgency.color, textAlign: 'right' }}>
                  {urgency.label}
                </div>
              </div>
              <div style={{ marginBottom: theme.spacing.sm }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <span style={{ color: theme.colors.primary }}>📍</span>
                  <span style={{ color: theme.colors.text, fontWeight: 700, fontSize: 15 }}>{match.location}</span>
                </div>
                <span style={{ color: theme.colors.textSecondary, fontSize: 11 }}>{formatFinishedDate(match.finishedAt)}</span>
              </div>
              <div style={{ marginBottom: theme.spacing.sm, fontSize: 12, color: theme.colors.textSecondary, fontWeight: 600 }}>
                Te falta:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {match.actions.map((action, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>
                      {action.type === 'REPORT_STATS' ? '📊' : '⭐'}
                    </span>
                    <span style={{ color: theme.colors.text, fontSize: 13 }}>{action.label}</span>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: theme.spacing.sm,
                  paddingTop: theme.spacing.sm,
                  borderTop: `1px solid ${theme.colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  color: theme.colors.primary,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Ir al partido →
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
